import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const SettingsModal = ({ 
    isOpen, 
    onClose, 
    user, 
    onLoginSuccess, 
    onLoginError, 
    onLogout,
    showBackButton = true,
    backButtonText = "← Back to Game",
    onBackClick = null
}) => {
    const navigate = useNavigate();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [walletAddress, setWalletAddress] = useState(null);

    // 檢查錢包連接狀態
    useEffect(() => {
        const checkWalletConnection = async () => {
            if (typeof window.ethereum !== 'undefined') {
                try {
                    const accounts = await window.ethereum.request({ method: 'eth_accounts' });
                    if (accounts.length > 0) {
                        const address = accounts[0];
                        setWalletAddress(address);
                        sessionStorage.setItem('zklogin_user_address', address);
                        if (onLoginSuccess) {
                            onLoginSuccess({ userAddress: address });
                        }
                    }
                } catch (error) {
                    console.error('檢查錢包連接失敗:', error);
                }
            }
        };
        
        if (isOpen) {
            checkWalletConnection();
        }
    }, [isOpen, onLoginSuccess]);

    // 監聽錢包帳戶變化
    useEffect(() => {
        if (typeof window.ethereum !== 'undefined') {
            const handleAccountsChanged = (accounts) => {
                if (accounts.length === 0) {
                    // 用戶斷開連接
                    setWalletAddress(null);
                    sessionStorage.removeItem('zklogin_user_address');
                    if (onLogout) {
                        onLogout();
                    }
                } else {
                    // 用戶切換帳戶
                    const address = accounts[0];
                    setWalletAddress(address);
                    sessionStorage.setItem('zklogin_user_address', address);
                    if (onLoginSuccess) {
                        onLoginSuccess({ userAddress: address });
                    }
                }
            };

            window.ethereum.on('accountsChanged', handleAccountsChanged);

            return () => {
                if (window.ethereum && window.ethereum.removeListener) {
                    window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
                }
            };
        }
    }, [onLoginSuccess, onLogout]);

    // 處理返回按鈕點擊
    const handleBackClick = () => {
        if (onBackClick) {
            onBackClick();
        } else {
            window.history.back();
        }
    };

    // 連接錢包
    const handleConnectWallet = async () => {
        if (typeof window.ethereum === 'undefined') {
            alert('Please install MetaMask or another Ethereum wallet extension!');
            if (onLoginError) {
                onLoginError(new Error('MetaMask not installed'));
            }
            return;
        }

        setIsConnecting(true);
        try {
            // 請求連接錢包
            const accounts = await window.ethereum.request({ 
                method: 'eth_requestAccounts' 
            });
            
            if (accounts.length > 0) {
                const address = accounts[0];
                setWalletAddress(address);
                sessionStorage.setItem('zklogin_user_address', address);
                
                console.log('✅ 錢包連接成功:', address);
                
                if (onLoginSuccess) {
                    onLoginSuccess({ userAddress: address });
                }
            }
        } catch (error) {
            console.error('❌ 錢包連接失敗:', error);
            if (onLoginError) {
                onLoginError(error);
            }
        } finally {
            setIsConnecting(false);
        }
    };

    // 斷開錢包連接
    const handleDisconnectWallet = () => {
        setWalletAddress(null);
        sessionStorage.removeItem('zklogin_user_address');
        
        if (onLogout) {
            onLogout();
        }
        
        console.log('✅ 錢包已斷開連接');
    };

    // ESC 關閉 modal
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, onClose]);

    // 如果 modal 未開啟，不渲染
    if (!isOpen) {
        return null;
    }

    return (
        <>
            {/* Modal Overlay */}
            <div 
                className="menu-modal open" 
                onClick={(e) => {
                    if (e.target.classList.contains('menu-modal')) {
                        onClose();
                    }
                }}
            >
                <div className="menu-content">
                    <div className="menu-items">
                        {/* 錢包連接區域 */}
                        <div className="menu-item">
                            <div className="menu-item-title">🔗 Connect Wallet</div>
                            <div className="menu-item-desc">
                                {walletAddress 
                                    ? `Connected: ${walletAddress.slice(0, 6)}...${walletAddress.slice(-4)}`
                                    : 'Connect your Ethereum wallet (MetaMask) to play'
                                }
                            </div>
                            
                            {walletAddress ? (
                                <>
                                    <div className="modal-user-info" style={{ marginBottom: '10px' }}>
                                        <div className="modal-user-address" style={{ wordBreak: 'break-all' }}>
                                            {walletAddress}
                                        </div>
                                    </div>
                                    <button 
                                        className="modal-logout-btn" 
                                        onClick={handleDisconnectWallet}
                                    >
                                        Disconnect Wallet
                                    </button>
                                </>
                            ) : (
                                <button 
                                    className="modal-action-btn" 
                                    onClick={handleConnectWallet}
                                    disabled={isConnecting}
                                    style={{ 
                                        opacity: isConnecting ? 0.6 : 1,
                                        cursor: isConnecting ? 'not-allowed' : 'pointer'
                                    }}
                                >
                                    {isConnecting ? (
                                        <>
                                            <span className="loading-spinner"></span>
                                            Connecting...
                                        </>
                                    ) : (
                                        'Connect MetaMask'
                                    )}
                                </button>
                            )}
                        </div>

                        {/* USDC 錢包 */}
                        {walletAddress && (
                            <div className="menu-item">
                                <div className="menu-item-title">💰 USDC Wallet</div>
                                <div className="menu-item-desc">Manage your USDC balance and transactions</div>
                                <button 
                                    className="modal-action-btn" 
                                    onClick={() => {
                                        onClose();
                                        navigate('/2048eth/wallet');
                                    }}
                                    style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                                >
                                    💰 Go to Wallet
                                </button>
                            </div>
                        )}

                        {/* USDC 錦標賽 */}
                        <div className="menu-item">
                            <div className="menu-item-title">💰 USDC 錦標賽</div>
                            <div className="menu-item-desc">Join tournaments and win USDC prizes!</div>
                            <a href="/2048eth/tournament" className="modal-action-btn tournament-btn">🏆 Join Tournament</a>
                        </div>

                        {/* 返回按鈕 (可選) */}
                        {showBackButton && (
                            <div className="menu-item">
                                <div className="menu-item-title">🔙 Navigation</div>
                                <button 
                                    className="modal-action-btn" 
                                    onClick={handleBackClick}
                                    style={{ background: '#6c757d' }}
                                >
                                    {backButtonText}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 樣式 */}
            <style>{`
                /* Hamburger Menu & Modal */
                .menu-modal { 
                    position: fixed; 
                    top: 0; 
                    left: 0; 
                    width: 100%; 
                    height: 100%; 
                    background: rgba(0,0,0,0.5); 
                    z-index: 1500; 
                    display: flex; 
                    align-items: center; 
                    justify-content: center; 
                    padding: 16px; 
                    opacity: 0; 
                    visibility: hidden; 
                    transition: all 0.3s; 
                }
                
                .menu-modal.open { 
                    opacity: 1; 
                    visibility: visible; 
                }
                
                .menu-content { 
                    background: #faf8ef; 
                    border-radius: 12px; 
                    padding: 28px; 
                    min-width: 320px; 
                    max-width: 400px; 
                    width: 90%; 
                    max-height: min(88vh, 720px); 
                    overflow-y: auto; 
                    color: #776e65; 
                    transform: scale(0.9); 
                    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); 
                }
                
                .menu-modal.open .menu-content { 
                    transform: scale(1); 
                }
                
                .menu-items { 
                    display: flex; 
                    flex-direction: column; 
                    gap: 15px; 
                }
                
                .menu-item { 
                    background: #eee4da; 
                    border-radius: 8px; 
                    padding: 15px 20px; 
                }
                
                .menu-item-title { 
                    font-weight: 600; 
                    margin-bottom: 5px; 
                    font-size: 1em;
                }
                
                .menu-item-desc { 
                    font-size: 0.85em; 
                    opacity: 0.8; 
                    margin-bottom: 10px; 
                }
                
                .modal-user-info { 
                    color: #776e65; 
                }
                
                .modal-user-name {
                    font-weight: 600;
                    font-size: 1em;
                }
                
                .modal-user-address {
                    font-family: monospace;
                    font-size: 0.8em;
                    opacity: 0.7;
                }
                
                .modal-logout-btn, .modal-action-btn { 
                    background: #8f7a66; 
                    color: #f9f6f2; 
                    border: none; 
                    border-radius: 3px; 
                    padding: 10px; 
                    width: 100%; 
                    cursor: pointer; 
                    text-align: center; 
                    text-decoration: none; 
                    display: inline-block; 
                    font-size: 0.9em;
                    transition: all 0.3s ease;
                }
                
                .modal-logout-btn:hover, .modal-action-btn:hover {
                    background: #9f8b77;
                    transform: translateY(-1px);
                }
                
                /* USDC 錦標賽按鈕特殊樣式 */
                .modal-action-btn.tournament-btn {
                    background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
                    color: #333;
                    font-weight: 600;
                    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
                }
                
                .modal-action-btn.tournament-btn:hover {
                    background: linear-gradient(135deg, #FFA500 0%, #FF8C00 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
                }
                
                /* LoginButton 相關樣式 */
                .login-button-container {
                    width: 100%;
                }
                
                .loading-spinner {
                    width: 16px;
                    height: 16px;
                    border: 2px solid #f9f6f2;
                    border-top: 2px solid transparent;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin-right: 8px;
                    display: inline-block;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .login-result {
                    margin-top: 12px;
                    padding: 12px;
                    border-radius: 6px;
                    background-color: #d4edda;
                    border: 1px solid #c3e6cb;
                    color: #155724;
                    font-size: 14px;
                    font-family: monospace;
                }
                
                .result-title {
                    font-weight: bold;
                    margin-bottom: 8px;
                }
                
                .result-footer {
                    margin-top: 8px;
                    font-size: 12px;
                    opacity: 0.8;
                }
                
                .login-result code {
                    background: rgba(0,0,0,0.1);
                    padding: 2px 4px;
                    border-radius: 3px;
                    font-family: monospace;
                }
                
                /* 響應式設計 */
                @media (max-width: 768px) {
                    .menu-content {
                        padding: 20px;
                        min-width: 280px;
                    }
                    
                    .menu-item {
                        padding: 12px 16px;
                    }
                    
                    .menu-item-title {
                        font-size: 0.9em;
                    }
                    
                    .menu-item-desc {
                        font-size: 0.8em;
                    }
                }
            `}</style>
        </>
    );
};

export default SettingsModal;
